<svelte:options runes={false} />

<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import { querystring } from 'svelte-spa-router';
	import { user_settings } from 'src/stores/account/user';
	import { loadSelectraState, selectraLiveTariffEnabled } from 'src/stores/selectra';
	import Tariffs from 'src/components/Settings/PowerPricesSettings/Tariffs.svelte';
	import Intervals from 'src/components/Settings/PowerPricesSettings/Intervals.svelte';
	import PeakPower from 'src/components/Settings/PowerPricesSettings/PeakPower.svelte';
	import BillingDateSettingsV2 from 'src/components/Settings/HomeManagement/BillingDateSettingsV2.svelte';
	import PreferredCurrencySettingsV2 from 'src/components/Settings/HomeManagement/PreferredCurrencySettingsV2.svelte';
	import { AlertV2, ButtonV2, Modal, Text } from '@Shelly/shelly-design-system';
	import { powerPricesToIntervals } from 'src/helpers/power_prices_helpers';
	import { isDevServerUser } from 'src/helpers/user_server_conditions';

	let showModal = false;
	let selectraStateLoadPromise: Promise<void>;

	const openModal = () => {
		showModal = true;

		if (!selectraStateLoadPromise) {
			selectraStateLoadPromise = loadSelectraState();
		}
	};

	$: powerPrices = powerPricesToIntervals(
		$user_settings.power_prices
			? JSON.parse($user_settings.power_prices)
			: { tariffs: {}, schedule: [], intervals: {} },
	);

	$: currency = $user_settings.currency || '';
	$: disableTariffs = $user_settings.hasOwnProperty('power_prices_live_tariff');
	$: liveTariffEnabled =
		$selectraLiveTariffEnabled || Boolean($user_settings?.power_prices_live_tariff);

	onMount(() => {
		if (new URLSearchParams($querystring).has('energyPrices')) {
			openModal();
		}
	});
</script>

<div class="d-flex gap-s justify-content-between align-items-center">
	<div>
		<Text styleType="body-medium" class="mb-xs">
			{$format('settings_energy_prices', { default: 'Energy prices' })}
		</Text>
		<Text styleType="body-small" class="text-color-muted">
			{$format('settings_energy_prices_description', {
				default: 'Set electricity tariffs and schedules for this home.',
			})}
		</Text>
	</div>

	<ButtonV2 size="m" variant="secondary" on:click={openModal}>
		{$format('button_open', { default: 'Open' })}
	</ButtonV2>
</div>

{#if showModal}
	<Modal
		{showModal}
		title={$format('settings_energy_prices', { default: 'Energy prices' })}
		on:close={() => (showModal = false)}
	>
		<svelte:fragment slot="content">
			<div class="d-flex flex-direction-column">
				<div>
					<Text element="h2" class="mb-m">
						{$format('title_electricity_tariff_details', {
							default: 'Electricity Tariff Details',
						})}
					</Text>
					{#if !currency}
						<AlertV2
							backgroundColor="color-00"
							class="mb-m"
							heading={$format('set_electricity_tariff_details', {
								default: 'Set details',
							})}
							message={$format('info_msg_electricity_tarrif', {
								default:
									'Currently you don’t have any information set  in Electricity Tariff. Set your billing date and preferred currency and get the benefit of monitoring your electricity value.',
							})}
						/>
					{/if}
					<div class="d-flex flex-direction-column gap-m">
						<BillingDateSettingsV2 />
						<PreferredCurrencySettingsV2 />
					</div>
				</div>

				{#if isDevServerUser(['repo', 't1', 'nb'])}
					<hr class="mt-m mb-m" />
					<div>
						<PeakPower />
					</div>
				{/if}

				{#if powerPrices}
					<hr class="mt-m mb-m" />
					<div>
						<Tariffs
							bind:powerPrices
							{currency}
							{disableTariffs}
							useSelectraTariffSelector
						/>
					</div>
					{#if !liveTariffEnabled}
						<hr class="mt-m mb-m" />
						<div>
							<Intervals bind:powerPrices bind:liveTariffEnabled {currency} />
						</div>
					{/if}
				{/if}
			</div>
		</svelte:fragment>

		<div slot="footer" class="d-flex justify-content-end">
			<ButtonV2 variant="secondary" size="m" on:click={() => (showModal = false)}>
				{$format('button_close', { default: 'Close' })}
			</ButtonV2>
		</div>
	</Modal>
{/if}
